import type { ReactNode } from 'react';

const MAX_WIDTH = {
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl',
  '3xl': 'max-w-3xl',
  '5xl': 'max-w-5xl',
} as const;

/**
 * The shared chrome every write wizard on the platform wears.
 *
 * A dimmed backdrop, a bordered card, a header (with an optional step-pill row
 * for the stepped ones — the OMCI/TR-069 management-mode look), a scrolling
 * body, and a footer the caller fills with its own buttons. Ten dialogs
 * otherwise repeat these fifteen lines of identical markup verbatim; sharing
 * them is what makes "every wizard looks like the OMCI/TR-069 one" true by
 * construction rather than by everyone copying the same divs.
 *
 * PRESENTATIONAL ONLY, deliberately. The step STATE — which step is current,
 * what each validates, when the dry-run fires — is genuinely per-dialog and
 * stays in the dialog; folding it in here would trade real duplication for a
 * leaky abstraction. The caller passes `steps` + `currentStep` to render the
 * pill row and its own `footer` buttons; everything else the shell owns.
 */
export function WizardDialog({
  title,
  subtitle,
  maxWidth = 'lg',
  steps,
  currentStep = 0,
  footer,
  onClose,
  children,
  bodyClassName = 'max-h-[65vh] space-y-4 overflow-y-auto p-5',
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  maxWidth?: keyof typeof MAX_WIDTH;
  /** Present for a stepped wizard: renders the pill row the reference look has. */
  steps?: Array<{ key: string; title: string }>;
  currentStep?: number;
  /** The caller's own buttons — they differ too much to standardise. */
  footer?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  /**
   * Overrides the body wrapper's classes. The default fits a standard dialog;
   * a taller workbench (the migration table) passes its own height and spacing.
   */
  bodyClassName?: string;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 sm:p-8">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div
        className={`relative w-full ${MAX_WIDTH[maxWidth]} rounded-xl border border-line bg-surface shadow-2xl`}
      >
        <header className="border-b border-line px-5 py-4">
          <h2 className="text-sm font-semibold text-ink">{title}</h2>
          {subtitle && <p className="mt-0.5 text-xs text-muted">{subtitle}</p>}
          {steps && steps.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {steps.map((s, i) => (
                <span
                  key={s.key}
                  className={`rounded-full px-2 py-0.5 text-[11px] ${
                    i === currentStep
                      ? 'bg-primary-soft text-primary'
                      : i < currentStep
                        ? 'text-muted'
                        : 'text-faint'
                  }`}
                >
                  {s.title}
                </span>
              ))}
            </div>
          )}
        </header>
        <div className={bodyClassName}>{children}</div>
        {/* The shell owns the border and padding; the caller lays its own
            buttons out (right-aligned, or space-between with a Back on the
            left) because those genuinely differ between wizards. */}
        {footer && <footer className="border-t border-line px-5 py-4">{footer}</footer>}
      </div>
    </div>
  );
}
